import { CalculateServiceCostResult, Service } from '@anispi-reporting-system/types';
import { ErrorOutline } from '@mui/icons-material';
import { Card, FormControl, Grid, InputLabel, MenuItem, Select } from '@mui/material';
import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import React, { FunctionComponent, useEffect } from 'react';
import { UploadCsvFile } from './UploadCsvFile';
import type { UploadFile } from '../hooks/useForm';
import { labels } from '../lib/labels';

interface Props {
  companyName: string;
  companyNo: string;
  officeNo: string;
  service: Service;
  setService: React.Dispatch<React.SetStateAction<Service>>;
  setServiceCost: React.Dispatch<React.SetStateAction<CalculateServiceCostResult['serviceCost'] | undefined>>;
  setUploadFileSe: React.Dispatch<React.SetStateAction<UploadFile>>;
  setUploadFileSv: React.Dispatch<React.SetStateAction<UploadFile>>;
  uploadFileSe: UploadFile;
  uploadFileSv: UploadFile;
}

const ServiceAndUploadCsv: FunctionComponent<Props> = ({
  companyName,
  companyNo,
  officeNo,
  service,
  setService,
  setServiceCost,
  setUploadFileSe,
  setUploadFileSv,
  uploadFileSe,
  uploadFileSv,
}) => {
  const handleChange = (serviceValue: Service) => {
    setService(serviceValue);
    setServiceCost(undefined);
  };

  useEffect(() => {
    setUploadFileSe({
      filename: '',
      uploadKey: '',
    });
    setUploadFileSv({
      filename: '',
      uploadKey: '',
    });
  }, [service, setUploadFileSe, setUploadFileSv]);

  return (
    <Box className="w-full">
      <Typography color="text.primary" variant="h6" gutterBottom>
        国保連伝送用CSVファイル
      </Typography>

      <Box className="relative mb-6">
        <Card variant="outlined">
          <CardContent className="space-y-2">
            <Grid container spacing={2} rowSpacing={3}>
              {(!companyName || !officeNo || !companyNo) && (
                <Box className="absolute left-0 top-0 z-10 flex size-full flex-row items-center justify-center bg-gray-300/80">
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      企業名,企業No,事業所番号を指定してください
                    </Typography>
                  </Box>
                </Box>
              )}
              <Grid item xs={12}>
                <FormControl className="w-1/2">
                  <InputLabel>サービスの種類</InputLabel>
                  <Select value={service} label="サービスの種類" onChange={e => handleChange(e.target.value as Service)}>
                    <MenuItem value="RS">{labels.Service.RS}</MenuItem>
                    <MenuItem value="RY">{labels.Service.RY}</MenuItem>
                    <MenuItem value="RYSHORT">{labels.Service.RYSHORT}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid>
              <Typography variant="subtitle2" color="error" className="flex items-center">
                <ErrorOutline />
                CSV以外のファイルは選択できません。Excelで保存したファイルは選択できません。
              </Typography>
            </Grid>
            <UploadCsvFile
              title={service === 'HOMECARE-RY' || service === 'HOMECARE-RS' ? '【保険内】' : '【SE】'}
              companyName={companyName}
              companyNo={companyNo}
              officeNo={officeNo}
              service={service}
              setService={setService}
              setUploadFile={setUploadFileSe}
              uploadFile={uploadFileSe}
              uploadFileType={service === 'HOMECARE-RY' || service === 'HOMECARE-RS' ? 'INSURANCE' : 'SE'}
            />
            <UploadCsvFile
              title={service === 'HOMECARE-RY' || service === 'HOMECARE-RS' ? '【保険外】' : '【SV】'}
              companyName={companyName}
              companyNo={companyNo}
              officeNo={officeNo}
              service={service}
              setService={setService}
              setUploadFile={setUploadFileSv}
              uploadFile={uploadFileSv}
              uploadFileType={service === 'HOMECARE-RY' || service === 'HOMECARE-RS' ? 'UNINSURANCE' : 'SV'}
            />
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export { ServiceAndUploadCsv };
